import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';

const BASE_URL = '/api/library';

const toQuery = (params = {}) => {
  const query = new URLSearchParams(params).toString();
  return query ? `?${query}` : '';
};

const apiThunk = (type, request) =>
  createAsyncThunk(type, async (arg, { rejectWithValue }) => {
    try {
      const response = await request(arg);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  });

// Library Items (books). Copies are managed with `number_of_copies` on the item.
export const fetchLibraryItems = apiThunk('library/fetchLibraryItems',
  (params) => axiosInstance.get(`${BASE_URL}/items/${toQuery(params)}`));

export const createLibraryItem = apiThunk('library/createLibraryItem',
  (itemData) => axiosInstance.post(`${BASE_URL}/items/`, itemData));

export const updateLibraryItem = apiThunk('library/updateLibraryItem',
  ({ id, ...itemData }) => axiosInstance.patch(`${BASE_URL}/items/${id}/`, itemData));

export const deleteLibraryItem = createAsyncThunk(
  'library/deleteLibraryItem',
  async (id, { rejectWithValue }) => {
    try {
      await axiosInstance.delete(`${BASE_URL}/items/${id}/`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Members are created automatically on the first borrow (read only)
export const fetchLibraryMembers = apiThunk('library/fetchLibraryMembers',
  (params) => axiosInstance.get(`${BASE_URL}/members/${toQuery(params)}`));

// Borrow Transactions
export const fetchBorrowTransactions = apiThunk('library/fetchBorrowTransactions',
  (params) => axiosInstance.get(`${BASE_URL}/transactions/${toQuery(params)}`));

// { item_id, student_id } or { item_id, employee_id }
export const borrowBook = apiThunk('library/borrowBook',
  (data) => axiosInstance.post(`${BASE_URL}/transactions/borrow/`, data));

export const returnBook = apiThunk('library/returnBook',
  (transactionId) => axiosInstance.post(`${BASE_URL}/transactions/${transactionId}/return_book/`));

// Library Settings
export const fetchLibrarySettings = apiThunk('library/fetchLibrarySettings',
  () => axiosInstance.get(`${BASE_URL}/settings/current/`));

export const updateLibrarySettings = apiThunk('library/updateLibrarySettings',
  (settingsData) => axiosInstance.patch(`${BASE_URL}/settings/current/`, settingsData));

// Dashboard & Reports
export const fetchLibraryDashboard = apiThunk('library/fetchLibraryDashboard',
  () => axiosInstance.get(`${BASE_URL}/dashboard/`));

export const fetchLibraryReports = apiThunk('library/fetchLibraryReports',
  (params) => axiosInstance.get(`${BASE_URL}/reports/${toQuery(params)}`));

// Late borrows for the dashboard (not stored in redux)
export const fetchOverdueList = async () => {
  const response = await axiosInstance.get(`${BASE_URL}/transactions/${toQuery({ overdue_only: 'true' })}`);
  return response.data;
};

// Students / employees to pick from when borrowing (not stored in redux)
export const searchBorrowers = async (type, search = '') => {
  const response = await axiosInstance.get(`${BASE_URL}/borrowers/${toQuery({ type, search })}`);
  return response.data;
};

const librarySlice = createSlice({
  name: 'library',
  initialState: {
    items: [],
    members: [],
    transactions: [],
    settings: null,
    dashboard: null,
    reports: null,
    loading: false,
    saving: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    const trackLoading = (thunk, key) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.loading = true;
          state.error = null;
        })
        .addCase(thunk.fulfilled, (state, action) => {
          state.loading = false;
          state[key] = action.payload;
        })
        .addCase(thunk.rejected, (state, action) => {
          state.loading = false;
          state.error = action.payload;
        });
    };
    const trackSaving = (thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state) => {
          state.saving = false;
        });
    };

    trackLoading(fetchLibraryItems, 'items');
    trackLoading(fetchLibraryMembers, 'members');
    trackLoading(fetchBorrowTransactions, 'transactions');
    trackLoading(fetchLibraryDashboard, 'dashboard');
    trackLoading(fetchLibraryReports, 'reports');

    [createLibraryItem, updateLibraryItem, deleteLibraryItem, borrowBook, returnBook]
      .forEach(trackSaving);

    builder
      .addCase(fetchLibrarySettings.fulfilled, (state, action) => {
        state.settings = action.payload;
      })
      .addCase(updateLibrarySettings.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateLibrarySettings.fulfilled, (state, action) => {
        state.saving = false;
        state.settings = action.payload;
      })
      .addCase(updateLibrarySettings.rejected, (state) => {
        state.saving = false;
      });
  },
});

export const { clearError } = librarySlice.actions;
export default librarySlice.reducer;
